import React, { useState } from "react";
import apiClient from "../../business/network/ApiClient";

function LegalModal({ type, content, onClose }) {

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-800">
                        {type === "terms" ? "Terms of Use" : "Privacy Policy"}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {!content && <p className="text-gray-500">No content available</p>}
                    {content && (
                        <div
                            className="prose prose-sm max-w-none text-gray-700 space-y-4 text-justify"
                            dangerouslySetInnerHTML={{ __html: content }}
                        />
                    )}
                </div>

                <div className="flex justify-end p-6 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-dark-blue-100 text-white rounded-lg font-semibold hover:bg-blue-800 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function TermsAcceptanceModal({ onAccept, onCancel }) {
    const [isChecked, setIsChecked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openModal, setOpenModal] = useState(null);
    const [legalContent, setLegalContent] = useState(null);
    const [loadingContent, setLoadingContent] = useState(true);

    React.useEffect(() => {
        const fetchLegalContent = async () => {
            try {
                setLoadingContent(true);
                const data = await apiClient.getTermsOfUse();
                if (data.privacyPolicy) {
                    data.privacyPolicy = data.privacyPolicy.replaceAll('24.0pt', '20px');
                    data.privacyPolicy = data.privacyPolicy.replaceAll('9.0pt', '14px');
                    data.privacyPolicy = data.privacyPolicy.replaceAll('en-IL', 'en-GB');
                    data.privacyPolicy = data.privacyPolicy.replaceAll('#2AD156', 'rgb(17, 24, 39)');
                    data.privacyPolicy = data.privacyPolicy.replaceAll('&quot;Arial&quot;', '-apple-system, BlinkMacSystemFont, "Noto Sans", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif');
                    data.privacyPolicy = data.privacyPolicy.replaceAll(',sans-serif', '');
                    data.privacyPolicy = data.privacyPolicy.replaceAll('mso-fareast-font-family:&quot;Times New Roman&quot;', '');
                    data.privacyPolicy = data.privacyPolicy.replaceAll('14.4pt;', 'none');
                    data.privacyPolicy = data.privacyPolicy.replaceAll('letter-spacing:.75pt;', 'letter-spacing:none;');
                }

                //header 20px
                //#2AD156 by rgb(17, 24, 39) 
                //font size 14px

                setLegalContent(data);
            } catch (err) {
                console.error("Failed to load legal content:", err);
            } finally {
                setLoadingContent(false);
            }
        };

        fetchLegalContent();
    }, []);

    const handleAccept = async () => {
        if (!isChecked || isSubmitting) return;

        setIsSubmitting(true);
        try {
            await onAccept();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (!isSubmitting) {
            onCancel();
        }
    };

    const handleOpenModal = (type) => {
        setOpenModal(type);
    };

    return (
        <>
            <div className="w-full md:min-w-[520px]" style={{ minHeight: '300px' }}>
                <div className="text-xl text-dimgray-200 font-bold py-4 px-6 border-b border-whitesmoke-100">
                    Before you start with AskMila™
                </div>

                <div className="gap-0 flex flex-col p-6">
                    <div className="flex flex-col gap-4">
                        <p className="text-dimgray-200 leading-relaxed">
                            By checking this box, I acknowledge and agree that:
                        </p>

                        <ul className="list-disc pl-5 text-dimgray-200 space-y-2">
                            <li>I'm aware that AskMila™ is an Artificial Intelligence tool</li>
                            <li>I agree to use AskMila™ responsibly and not rely solely on its outputs for critical decisions</li>
                            <li>
                                My use of AskMila™ is subject to{" "}
                                <strong>
                                    GreenRoad’s Terms of Use
                                </strong>{" "}and to the AI Terms Addendum.
                            </li>
                        </ul>

                        <div className="flex items-start gap-3 mt-4 p-4 bg-gray-50 rounded">
                            <input
                                type="checkbox"
                                id="terms-checkbox"
                                checked={isChecked}
                                onChange={(e) => setIsChecked(e.target.checked)}
                                className="mt-1 w-4 h-4 cursor-pointer"
                                disabled={isSubmitting}
                            />
                            <label
                                htmlFor="terms-checkbox"
                                className="text-dimgray-200 cursor-pointer select-none"
                            >
                                I have read and agree to{" "}
                                <button
                                    onClick={() => handleOpenModal("terms")}
                                    disabled={loadingContent}
                                    className="text-dark-blue-100 underline hover:text-blue-700 bg-none border-none cursor-pointer disabled:opacity-50 text-wrap"
                                >
                                    GreenRoad’s Terms of Use
                                </button>{" "}and to the AI Terms Addendum.
                            </label>
                        </div>
                    </div>

                    <div className="flex gap-3 mt-8 justify-end">
                        <button
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="border rounded-2xl py-3 px-6 border-dimgray-200 text-dark-blue-100 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel & Exit
                        </button>
                        <button
                            onClick={handleAccept}
                            disabled={!isChecked || isSubmitting}
                            className="border rounded-2xl py-3 px-6 border-dimgray-200 bg-dark-blue-100 text-white font-semibold hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                        >
                            {isSubmitting ? "Processing..." : "Agree & Continue"}
                        </button>
                    </div>
                </div>
            </div>

            {openModal && (
                <LegalModal
                    type={openModal}
                    content={openModal === "terms" ? legalContent?.termsOfUse : legalContent?.privacyPolicy}
                    onClose={() => setOpenModal(null)}
                />
            )}
        </>
    );
}